import { Dialog } from '../../components/dialog-portal/dialog.tsx';


interface Props {
    show: boolean;
    onClose: () => void;
}

export function SpcInfoDialog({ show, onClose }: Props) {
    if (!show) return null;
    return (
        <Dialog title='SPC Details — Guide' onClose={onClose}>
            <div style={{ maxWidth: '580px', maxHeight: '70vh', overflowY: 'auto', paddingRight: 6 }}>

            <p>
                This page shows <strong>Statistical Process Control (SPC)</strong> data for a specific
                part and machine. SPC is a method of tracking whether a manufacturing process stays
                within acceptable limits over time.
            </p>

            <h3>The position table (right side)</h3>
            <p>
                The table lists every measurement position defined in the protocol. Each row shows:
            </p>
            <ul>
                <li><strong>Position name</strong> — the measured feature (e.g. diameter, length).</li>
                <li><strong>Status indicators</strong> — coloured cells representing which SPC checks
                    pass or fail. Click the column headers to toggle individual checks on or off.</li>
            </ul>
            <p>Click any row to load its measurement graph and statistics in the main area.</p>

            <h3>The measurement graph</h3>
            <p>
                The graph plots individual measurement values (Y axis) against cumulative part count
                (X axis). The following reference lines may be shown (toggle them with the controls
                above the graph):
            </p>
            <ul>
                <li><strong>Tolerance lines</strong> — the specification limits from the protocol.
                    Values outside these are non-conforming.</li>
                <li><strong>Mean line</strong> — the average of all displayed measurements.</li>
                <li><strong>Sigma bands (1σ / 2σ / 3σ)</strong> — natural process variation ranges
                    computed from the data. In a stable process ~68 % of values fall within 1σ,
                    ~95 % within 2σ, and ~99.7 % within 3σ.</li>
                <li><strong>Batch markers</strong> — vertical lines separating production batches.</li>
            </ul>

            <h3>Sigma histogram</h3>
            <p>
                The histogram below the graph shows the distribution of measurements relative to the
                sigma bands. A bell-shaped distribution centred around the mean is a sign of a stable,
                capable process.
            </p>

            <h3>Filters (top of main area)</h3>
            <ul>
                <li>
                    <strong>Data range</strong> — restrict the data to a specific batch, order number,
                    or date interval. Selecting a narrower range re-calculates all statistics.
                </li>
                <li>
                    <strong>Measurement plan</strong> — filter by measurement variant (A, B, C, …).
                    Different variants correspond to different sampling plans used during production
                    (e.g. A = regular check, B = extended check, C = full check). Options are greyed
                    out when no measurements of that type exist in the current data range.
                </li>
            </ul>

            <h3>Position details panel</h3>
            <p>
                When a position is selected, a panel shows its nominal value, computed mean, standard
                deviation and sigma limits. You can optionally override the mean or standard deviation
                to simulate what-if scenarios without changing the stored data.
            </p>

            </div>
        </Dialog>
    );
}
